import { Step, StepLabel, Stepper } from "@mui/material";
import moment from "moment";

import { PRODUCT_STATUSES } from "api/memberPortal/project/product/productStatuses.type";
import {
  useGetAllGroupedProgressPointsQuery,
  useGetAllGroupedMaintenanceProgressPointsQuery,
} from "api/memberPortal/project/progressPoints/progressPoints.api";

const ALL_PROGRESS_POINTS = {};
const ALL_MAINTENANCE_PROGRESS_POINTS = {};

export default function StageTracker({ rowData }) {
  const { data: progressPoints = ALL_PROGRESS_POINTS } =
    useGetAllGroupedProgressPointsQuery();
  const { data: maintenanceProgressPoints = ALL_MAINTENANCE_PROGRESS_POINTS } =
    useGetAllGroupedMaintenanceProgressPointsQuery();

  const draftSteps = ["Checkin Package saved as Draft. Not sent to Vendors."];

  const proposedSteps = [
    "Submitted Checkin Package for Review",
    "Checkin Package Signed",
    "NIAP Approved and Ready for Assignment",
    "Validator Team Submitted",
  ];

  const preEvalSteps = [
    "Validator Team Approved by NIAP",
    "Kickoff Recommended by Validator",
    "Kickoff Approved/Evaluation Initiated",
  ];

  const inProgressSteps = [
    "Evaluator Checkout Package Received",
    "Validator Checkout Complete",
    "Validator Recommend for Posting",
  ];

  const finalizingSteps = [
    "Validator Checkout Package Accepted",
    "PCL Posting",
  ];

  const certifiedSteps = ["Completed"];

  const terminatedSteps = ["Terminated"];

  const withdrawnSteps = ["Withdrawn"];

  const archivedSteps = ["Archived"];

  const assuranceMaintenanceSteps = [
    "AM Requested",
    "Ready for Assignment",
    "IAR Review Assigned",
    "ACMR Approved",
    "ACMR Posted",
  ];

  const getPhaseSteps = (status) => {
    switch (status) {
      case PRODUCT_STATUSES.DRAFT:
        return draftSteps;
      case PRODUCT_STATUSES.PRE_PROPOSAL:
        return proposedSteps;
      case PRODUCT_STATUSES.PROPOSED:
        return proposedSteps;
      case PRODUCT_STATUSES.PRE_EVALUATION:
        return preEvalSteps;
      case PRODUCT_STATUSES.IN_PROGRESS:
        return inProgressSteps;
      case PRODUCT_STATUSES.FINALIZING:
        return finalizingSteps;
      case PRODUCT_STATUSES.CERTIFIED:
        return certifiedSteps;
      case PRODUCT_STATUSES.TERMINATED:
        return terminatedSteps;
      case PRODUCT_STATUSES.WITHDRAWN:
        return withdrawnSteps;
      case PRODUCT_STATUSES.ARCHIVED:
        return archivedSteps;
      case PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE:
        return assuranceMaintenanceSteps;
      default:
        return ["Other"];
    }
  };

  const getPhaseName = (status) => {
    switch (status) {
      case PRODUCT_STATUSES.DRAFT:
        return "In Draft";
      case PRODUCT_STATUSES.PRE_PROPOSAL:
        return "Pre-Proposal";
      case PRODUCT_STATUSES.PROPOSED:
        return "Phase 1: Proposed";
      case PRODUCT_STATUSES.PRE_EVALUATION:
        return "Phase 2: Pre-Evaluation";
      case PRODUCT_STATUSES.IN_PROGRESS:
        return "Phase 3: In Progress";
      case PRODUCT_STATUSES.FINALIZING:
        return "Phase 4: Finalizing";
      case PRODUCT_STATUSES.CERTIFIED:
        return "Phase 4: Completed";
      case PRODUCT_STATUSES.TERMINATED:
        return "Terminated";
      case PRODUCT_STATUSES.WITHDRAWN:
        return "Withdrawn";
      case PRODUCT_STATUSES.ARCHIVED:
        return "Archived";
      case PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE:
        return "In Assurance Maintenance";
      default:
        return "Terminated";
    }
  };

  const getPhaseColor = (status) => {
    switch (status) {
      case PRODUCT_STATUSES.DRAFT:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.PRE_EVALUATION:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "info.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "info.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.IN_PROGRESS:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.CERTIFIED:
      case PRODUCT_STATUSES.FINALIZING:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "success.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "success.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.WITHDRAWN:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "warning.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.ARCHIVED:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "info.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "info.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      case PRODUCT_STATUSES.TERMINATED:
        return {
          "& .MuiStepLabel-root .Mui-completed": {
            color: "danger.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active": {
            color: "danger.main",
          },
          "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
            color: "black",
          },
          "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
            fill: "black",
          },
        };
      default:
        break;
    }
  };

  const getStage = (product) => {
    let relaventPPs = progressPoints[product?.product_id];

    let relaventMaintenancePPs =
      maintenanceProgressPoints[
        product?.current_assurance_maintenance?.maintenance_id
      ];

    if (!relaventPPs) {
      relaventPPs = [];
    }
    if (!relaventMaintenancePPs) {
      relaventMaintenancePPs = [];
    }
    switch (product.status) {
      case PRODUCT_STATUSES.DRAFT:
        return 1;
      case PRODUCT_STATUSES.PROPOSED:
        if (product?.pre_eval_status === "Proposed") {
          return 1; //lab submits checkin package
        } else if (product?.pre_eval_status === "Signed") {
          return 2; //vendor signs
        } else if (product?.pre_eval_status === "Ready for Assignment") {
          return 3; //niap accepts checkin package
        } else if (product?.pre_eval_status === "Submitted") {
          return 4; //validator assigns team
        }
        return 0;
      case PRODUCT_STATUSES.PRE_EVALUATION:
        if (
          product?.pre_eval_status === "Approved" &&
          product?.kickoff_status === "Ready for Recommendation"
        ) {
          return 1; //niap approved validation team
        } else if (product?.kickoff_status === "Recommended for Kickoff") {
          return 2; //kickoff recommended by validator
        } else if (
          product?.kickoff_status === "Kicked Off" &&
          relaventPPs[1]?.status !== "In Progress"
        ) {
          return 3; //niap approved kickoff
        }
        return 0;
      case PRODUCT_STATUSES.IN_PROGRESS:
        if (relaventPPs[6]?.status === "Completed") {
          return 3; //validator recommended for posting
        } else if (relaventPPs[6]?.status === "Ready for Release") {
          return 2; //validator checkout complete
        } else if (relaventPPs[5]?.status === "Awaiting Checkout") {
          return 1; //Evaluator Checkout Package Received
        }
        return 0;
      case PRODUCT_STATUSES.FINALIZING:
        if (relaventPPs[7]?.status === "In Progress") {
          return 1; //validator Checkout Package accepted
        } else if (relaventPPs[7]?.status === "Completed") {
          return 2; //pcl posting
        }
        return 0;
      case PRODUCT_STATUSES.CERTIFIED:
        return 1; //certified
      case PRODUCT_STATUSES.WITHDRAWN:
        return 1; //withdrawn
      case PRODUCT_STATUSES.ARCHIVED:
        return 1; //archived
      case PRODUCT_STATUSES.TERMINATED:
        return 1; //terminated
      case PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE:
        if (
          relaventMaintenancePPs[0]?.status === "In Progress" &&
          product.current_assurance_maintenance?.status !== "Released"
        ) {
          return 1; //AM Requested`
        } else if (
          product?.current_assurance_maintenance?.status === "Released" ||
          (relaventMaintenancePPs[0]?.status === "Completed" &&
            relaventMaintenancePPs[1]?.status === "Not Started")
        ) {
          return 2; //Ready for Assignment
        } else if (
          relaventMaintenancePPs[1]?.status === "Completed" &&
          relaventMaintenancePPs[2]?.status === "Not Started"
        ) {
          return 3; //IAR Review Assigned
        } else if (relaventMaintenancePPs[2]?.status === "In Progress") {
          return 3; //ACMR Approved
        } else if (relaventMaintenancePPs[2]?.status === "Completed") {
          return 4; //ACMR Posted
        } else {
          return 1;
        }
      default:
        return 1;
    }
  };

  return (
    <>
      <h5 className="text-center">{getPhaseName(rowData?.status)}</h5>
      <Stepper activeStep={getStage(rowData)} alternativeLabel>
        {getPhaseSteps(rowData?.status).map((label) => (
          <Step key={label} sx={getPhaseColor(rowData?.status)}>
            <StepLabel icon={" "}>
              {label}
              <br />
              {label === "Kickoff" && rowData?.kicked_off_date
                ? moment.utc(rowData?.kicked_off_date).format("MM/DD/YYYY")
                : ""}
              {label === "PCL Posting" && rowData?.certification_date
                ? moment.utc(rowData?.certification_date).format("MM/DD/YYYY")
                : ""}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </>
  );
}
